import { BusinessObjectBase } from './base';
import type { PendingStateUpdate } from './pending-state-update';
import type { ProcessDesign } from './process-design';
import type { ProcessInstanceHistory } from './process-instance-history';
import type { Token } from './token';

export interface ProcessInstanceData {
  readonly id: string;
  readonly referenceNumber?: number;
  readonly name?: string;
  readonly status?: string;
  readonly data?: any;
  readonly errorMessage?: string;
  readonly claimedBy?: string;
  readonly claimedAt?: Date;
  readonly completedAt?: Date;
  readonly processDesignId: number;
  readonly processDesign?: ProcessDesign;
  readonly parentTokenId: number;
  readonly parentToken?: Token;
  readonly processInstanceHistories?: ProcessInstanceHistory[];
  readonly pendingStateUpdates?: PendingStateUpdate[];
  readonly tokens?: Token[];
  readonly callingTokens?: Token[];
}

export interface ProcessInstance extends Readonly<ProcessInstanceData> {}
export class ProcessInstance extends BusinessObjectBase {
  static readonly entityName = 'ProcessInstance' as const;
  static readonly plural = 'process-instances';

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ProcessInstance[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ProcessInstance(data));
  }

  static async findOne(filter?: any): Promise<ProcessInstance | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ProcessInstance(data) : null;
  }

  static async findById(id: string, filter?: any): Promise<ProcessInstance> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ProcessInstance(data);
  }

  static async create(
    data: Omit<ProcessInstanceData, 'id'>,
  ): Promise<ProcessInstance> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ProcessInstance(result);
  }

  static async updateById(
    id: string,
    data: Partial<ProcessInstanceData>,
  ): Promise<ProcessInstance> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const result = await this.patch<any>(url, data);
    return new ProcessInstance(result);
  }

  static async deleteById(id: string): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  static async waitForStable(options: any): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/wait-for-stable`;
    return this.post<any>(url, options);
  }
  static async waitForStableAlreadyClaimed(options: any): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/wait-for-stable-already-claimed`;
    return this.post<any>(url, options);
  }
  async updateState(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessInstance.plural}/${(this as any).id}/update-state`;
    return BusinessObjectBase.post<any>(url, options);
  }
  async tryClaim(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessInstance.plural}/${(this as any).id}/try-claim`;
    return BusinessObjectBase.post<any>(url, options);
  }
  async releaseClaim(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessInstance.plural}/${(this as any).id}/release-claim`;
    return BusinessObjectBase.post<any>(url, options);
  }
  async extendClaim(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessInstance.plural}/${(this as any).id}/extend-claim`;
    return BusinessObjectBase.post<any>(url, options);
  }
  static async claimNext(options: any): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/claim-next`;
    return this.post<any>(url, options);
  }
  async updateData(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessInstance.plural}/${(this as any).id}/update-data`;
    return BusinessObjectBase.post<any>(url, options);
  }
  static async start(options: {
    designId: string;
    data?: Record<string, unknown>;
    wait?: number;
  }): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/start`;
    return this.post<any>(url, options);
  }
  static async startFromName(options: {
    name: string;
    data?: Record<string, unknown>;
    wait?: number;
  }): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/start-from-name`;
    return this.post<any>(url, options);
  }
  async sendMessage(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessInstance.plural}/${(this as any).id}/send-message`;
    return BusinessObjectBase.post<any>(url, options);
  }
  async retry(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessInstance.plural}/${(this as any).id}/retry`;
    return BusinessObjectBase.post<any>(url, {});
  }
  async resume(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessInstance.plural}/${(this as any).id}/resume`;
    return BusinessObjectBase.post<any>(url, {});
  }
  async processUpdates(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessInstance.plural}/${(this as any).id}/process-updates`;
    return BusinessObjectBase.post<any>(url, options);
  }
  async pause(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessInstance.plural}/${(this as any).id}/pause`;
    return BusinessObjectBase.post<any>(url, {});
  }
  async fail(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessInstance.plural}/${(this as any).id}/fail`;
    return BusinessObjectBase.post<any>(url, options);
  }
}
